import React, { useState, useEffect, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import {
  adminCreateEmailCampaign,
  adminUpdateEmailCampaign,
  adminCountEmailSegment,
  adminPreviewEmailCampaign,
  adminTestEmailCampaign,
  adminAutoTranslate,
} from "./adminClient";

/* ─── Constants ──────────────────────────────────────────────────────── */
const LANGS = [
  { code:"fr", label:"FR", flag:"🇫🇷" },
  { code:"en", label:"EN", flag:"🇬🇧" },
  { code:"es", label:"ES", flag:"🇪🇸" },
  { code:"de", label:"DE", flag:"🇩🇪" },
  { code:"pt", label:"PT", flag:"🇵🇹" },
  { code:"ar", label:"AR", flag:"🇸🇦" },
];

// Pays principaux (extensible — utilise ISO 3166-1 alpha-2)
const COUNTRIES = [
  { code:"FR", name:"France" },
  { code:"CI", name:"Côte d'Ivoire" },
  { code:"SN", name:"Sénégal" },
  { code:"CM", name:"Cameroun" },
  { code:"BJ", name:"Bénin" },
  { code:"TG", name:"Togo" },
  { code:"BF", name:"Burkina Faso" },
  { code:"ML", name:"Mali" },
  { code:"NE", name:"Niger" },
  { code:"GA", name:"Gabon" },
  { code:"CD", name:"RD Congo" },
  { code:"MA", name:"Maroc" },
  { code:"TN", name:"Tunisie" },
  { code:"DZ", name:"Algérie" },
  { code:"BE", name:"Belgique" },
  { code:"CH", name:"Suisse" },
  { code:"CA", name:"Canada" },
  { code:"US", name:"USA" },
  { code:"GB", name:"Royaume-Uni" },
  { code:"DE", name:"Allemagne" },
  { code:"ES", name:"Espagne" },
];

/* ─── Animations ─────────────────────────────────────────────────────── */
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;

/* ─── Styled ─────────────────────────────────────────────────────────── */
const Overlay = styled.div`
  position:fixed; inset:0; z-index:500;
  background:rgba(0,0,0,.82); backdrop-filter:blur(8px);
  display:flex; align-items:center; justify-content:center; padding:1rem;
  animation:${fadeIn} .15s ease;
`;
const ModalBox = styled.div`
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.borderLight};
  border-radius:16px; width:100%; max-width:780px;
  max-height:92vh; overflow-y:auto;
  animation:${slideUp} .2s ease;
`;
const MHead = styled.div`
  padding:1.1rem 1.4rem;
  border-bottom:1px solid ${({theme})=>theme.border};
  display:flex; align-items:center; justify-content:space-between;
  position:sticky; top:0;
  background:${({theme})=>theme.bgCard}; z-index:2;
`;
const MTitle = styled.h3`
  font-family:${({theme})=>theme.fontDisplay};
  font-size:1.05rem; font-weight:700; letter-spacing:-.02em;
`;
const MClose = styled.button`
  background:none; border:none;
  color:${({theme})=>theme.textMuted};
  font-size:1.1rem; cursor:pointer;
  &:hover { color:${({theme})=>theme.text}; }
`;
const MBody = styled.div`
  padding:1.25rem 1.4rem;
  display:flex; flex-direction:column; gap:1.25rem;
`;
const MFoot = styled.div`
  padding:.85rem 1.4rem;
  border-top:1px solid ${({theme})=>theme.border};
  display:flex; gap:.6rem; justify-content:flex-end; flex-wrap:wrap;
  position:sticky; bottom:0;
  background:${({theme})=>theme.bgCard};
`;

const Section = styled.div`
  display:flex; flex-direction:column; gap:.6rem;
  padding:1rem 1.1rem;
  background:${({theme})=>theme.bg};
  border:1px solid ${({theme})=>theme.border};
  border-radius:10px;
`;
const SectionTitle = styled.div`
  font-family:${({theme})=>theme.fontMono};
  font-size:.62rem; text-transform:uppercase; letter-spacing:.08em;
  color:${({theme})=>theme.textMuted};
  display:flex; align-items:center; justify-content:space-between;
  gap:.5rem;
`;

const Field = styled.div`display:flex; flex-direction:column; gap:.35rem;`;
const Label = styled.label`
  font-size:.67rem; text-transform:uppercase; letter-spacing:.08em;
  color:${({theme})=>theme.textMuted};
`;
const Required = styled.span`color:${({theme})=>theme.red}; font-size:.8rem;`;
const Input = styled.input`
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.border};
  border-radius:7px;
  color:${({theme})=>theme.text};
  font-family:${({theme})=>theme.fontBody};
  font-size:.82rem; padding:.55rem .8rem; outline:none;
  &:focus { border-color:${({theme})=>theme.borderLight}; }
  &::placeholder { color:${({theme})=>theme.textMuted}; }
`;
const Tarea = styled.textarea`
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.border};
  border-radius:7px;
  color:${({theme})=>theme.text};
  font-family:${({theme})=>theme.fontMono};
  font-size:.78rem; padding:.6rem .85rem; outline:none;
  resize:vertical; min-height:180px; line-height:1.55;
  &:focus { border-color:${({theme})=>theme.borderLight}; }
`;
const Select = styled.select`
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.border};
  border-radius:7px;
  color:${({theme})=>theme.text};
  font-family:${({theme})=>theme.fontBody};
  font-size:.82rem; padding:.55rem .8rem; outline:none;
  cursor:pointer;
`;
const Row2 = styled.div`
  display:grid; grid-template-columns:1fr 1fr; gap:.75rem;
  @media(max-width:560px) { grid-template-columns:1fr; }
`;

/* Language tabs (réutilisé de MarketFormModal) */
const LangTabs = styled.div`
  display:flex; gap:.3rem; flex-wrap:wrap;
  padding:.5rem;
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.border};
  border-radius:8px;
`;
const LangTab = styled.button`
  display:inline-flex; align-items:center; gap:.35rem;
  padding:.35rem .65rem; border-radius:6px;
  border:1px solid ${({$active,theme})=>$active?theme.accent:"transparent"};
  background:${({$active})=>$active?"rgba(79,195,247,.1)":"transparent"};
  color:${({$active,theme})=>$active?theme.accent:theme.textDim};
  font-family:${({theme})=>theme.fontMono};
  font-size:.68rem; font-weight:600;
  cursor:pointer; transition:all .15s;
  &:hover { color:${({theme})=>theme.text}; }
`;
const LangFlag = styled.span`font-size:.9rem; line-height:1;`;

const TranslateBtn = styled.button`
  display:inline-flex; align-items:center; gap:.35rem;
  padding:.35rem .8rem; border-radius:6px;
  border:1px solid ${({theme})=>theme.accent}66;
  background:rgba(79,195,247,.08);
  color:${({theme})=>theme.accent};
  font-size:.7rem; font-weight:600; cursor:pointer;
  &:hover:not(:disabled) { background:rgba(79,195,247,.15); }
  &:disabled { opacity:.5; cursor:wait; }
`;

const Chip = styled.button`
  display:inline-flex; align-items:center; gap:.25rem;
  padding:.3rem .65rem; border-radius:100px;
  border:1px solid ${({$active,theme})=>$active?theme.accent:theme.border};
  background:${({$active})=>$active?"rgba(79,195,247,.1)":"transparent"};
  color:${({$active,theme})=>$active?theme.accent:theme.textDim};
  font-family:${({theme})=>theme.fontMono};
  font-size:.65rem; cursor:pointer; transition:all .15s;
  &:hover { border-color:${({theme})=>theme.borderLight}; }
`;

const Radio = styled.label`
  display:inline-flex; align-items:center; gap:.4rem;
  font-size:.75rem;
  color:${({$active,theme})=>$active?theme.text:theme.textDim};
  cursor:pointer; user-select:none;
  input { cursor:pointer; }
`;

const Counter = styled.div`
  display:flex; align-items:center; justify-content:space-between;
  padding:.75rem 1rem;
  background:linear-gradient(90deg, rgba(79,195,247,.06), rgba(0,230,118,.06));
  border:1px solid ${({theme})=>theme.accent}44;
  border-radius:8px;
  font-size:.82rem;
`;
const CountVal = styled.span`
  font-family:${({theme})=>theme.fontMono};
  font-size:1.25rem; font-weight:700;
  color:${({theme,$zero})=>$zero?theme.red:theme.accent};
`;
const CountLbl = styled.span`color:${({theme})=>theme.textDim}; font-size:.72rem;`;

const SaveBtn = styled.button`
  padding:.55rem 1.2rem; border-radius:7px;
  border:none;
  background:${({theme,disabled,$variant}) =>
    disabled ? theme.bgHover :
    $variant === "send" ? theme.green :
    $variant === "test" ? theme.accent :
    theme.gold};
  color:${({theme,disabled,$variant}) =>
    disabled ? theme.textMuted :
    $variant ? "#000" : "#000"};
  font-size:.78rem; font-weight:700;
  cursor:${({disabled})=>disabled?"not-allowed":"pointer"};
  &:hover:not(:disabled) { opacity:.88; }
`;
const CancelBtn = styled.button`
  padding:.55rem 1rem; border-radius:7px;
  border:1px solid ${({theme})=>theme.border};
  background:transparent;
  color:${({theme})=>theme.textDim};
  font-size:.78rem; font-weight:600; cursor:pointer;
  &:hover { background:${({theme})=>theme.bgHover}; }
`;

/* Preview iframe wrapper */
const PreviewBox = styled.div`
  border:1px solid ${({theme})=>theme.borderLight};
  border-radius:8px; overflow:hidden;
  background:white;
`;

/* ─── Helpers ────────────────────────────────────────────────────────── */
const extractErr = (err, fallback="Erreur serveur.") => {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === "string") return data;
  if (data.detail) return data.detail;
  return JSON.stringify(data).slice(0, 200);
};

/* ════════════════════════════════════════════════════════════════════════
   MAIN FORM
════════════════════════════════════════════════════════════════════════ */
export default function EmailCampaignFormModal({ campaign, onClose, onSaved, toast }) {
  const editing = !!campaign;
  const isReadOnly = editing && !["DRAFT","QUEUED"].includes(campaign.status);

  /* ── State : contenu multilingue ──────────────────────────────────── */
  const [activeLang, setActiveLang] = useState("fr");
  const [name, setName] = useState(campaign?.name || "");

  // Initialiser titres et bodies pour chaque langue
  const initLangFields = (key) => {
    const result = {};
    LANGS.forEach(l => {
      result[l.code] = campaign?.[`${key}_${l.code}`] || "";
    });
    return result;
  };
  const [subjects, setSubjects] = useState(initLangFields("subject"));
  const [bodies,   setBodies]   = useState(initLangFields("body_html"));

  /* ── State : segmentation ─────────────────────────────────────────── */
  const [filters, setFilters] = useState(campaign?.filters || {
    status: "all",
    countries: [],
    languages: [],
    has_traded: null,
    has_deposited: null,
    phone_verified: null,
    balance_min: null,
    balance_max: null,
    include_admins: false,
    exclude_unsubscribed: true,
  });

  /* ── State : compteur live ────────────────────────────────────────── */
  const [recipientCount, setRecipientCount] = useState(null);
  const [counting, setCounting] = useState(false);
  const [previewSample, setPreviewSample] = useState([]);

  /* ── State : auto-traduction ──────────────────────────────────────── */
  const [translating, setTranslating] = useState(false);
  const [autoTranslating, setAutoTranslating] = useState(false);
  const [preserveManual, setPreserveManual] = useState(true);

  /* ── State : preview + actions ────────────────────────────────────── */
  const [previewModal, setPreviewModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  /* ── Compteur live (debounce 400ms) ───────────────────────────────── */
  useEffect(() => {
    const t = setTimeout(async () => {
      setCounting(true);
      try {
        const r = await adminCountEmailSegment(filters);
        setRecipientCount(r.data.count);
        setPreviewSample(r.data.preview || []);
      } catch {
        setRecipientCount(null);
      } finally {
        setCounting(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [filters]);

  /* ── Auto-traduction au changement d'onglet ────────────────────────── */
  useEffect(() => {
    if (activeLang === "fr") return;
    const sourceSubject = subjects.fr?.trim();
    const sourceBody    = bodies.fr?.trim();
    if (!sourceSubject && !sourceBody) return;
    if (subjects[activeLang]?.trim() && bodies[activeLang]?.trim()) return;

    let cancelled = false;

    const autoTr = async () => {
      setAutoTranslating(true);
      try {
        // Sujet
        if (sourceSubject && !subjects[activeLang]?.trim()) {
          const r = await adminAutoTranslate(sourceSubject, "fr");
          if (cancelled) return;
          const tr = r.data?.[activeLang];
          if (tr) setSubjects(s => s[activeLang]?.trim() ? s : ({...s, [activeLang]: tr}));
        }
        // Body
        if (sourceBody && !bodies[activeLang]?.trim()) {
          const r = await adminAutoTranslate(sourceBody, "fr");
          if (cancelled) return;
          const tr = r.data?.[activeLang];
          if (tr) setBodies(b => b[activeLang]?.trim() ? b : ({...b, [activeLang]: tr}));
        }
      } catch (err) {
        console.warn("Auto-translate failed:", err);
      } finally {
        if (!cancelled) setAutoTranslating(false);
      }
    };
    autoTr();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLang]);

  /* ── Auto-traduire tout en parallèle ──────────────────────────────── */
  const handleAutoTranslateAll = async () => {
    if (!subjects.fr.trim()) {
      toast.err("Saisis d'abord le sujet en français.");
      return;
    }
    setTranslating(true);
    try {
      const tasks = [];
      tasks.push({kind:"subject", source: subjects.fr.trim()});
      if (bodies.fr.trim()) tasks.push({kind:"body", source: bodies.fr.trim()});

      const results = await Promise.allSettled(
        tasks.map(async (task) => {
          const r = await adminAutoTranslate(task.source, "fr");
          return { task, data: r.data };
        })
      );

      let ok = 0, ko = 0;
      results.forEach(res => {
        if (res.status === "rejected") { ko++; return; }
        ok++;
        const { task, data } = res.value;
        if (task.kind === "subject") {
          setSubjects(s => {
            const next = { ...s };
            LANGS.forEach(l => {
              if (l.code === "fr") return;
              if (preserveManual && s[l.code]?.trim()) return;
              if (data[l.code]) next[l.code] = data[l.code];
            });
            return next;
          });
        } else {
          setBodies(b => {
            const next = { ...b };
            LANGS.forEach(l => {
              if (l.code === "fr") return;
              if (preserveManual && b[l.code]?.trim()) return;
              if (data[l.code]) next[l.code] = data[l.code];
            });
            return next;
          });
        }
      });

      if (ko === 0) toast.ok(`✓ ${ok} traductions terminées.`);
      else toast.err(`${ok} OK, ${ko} échecs.`);
    } finally {
      setTranslating(false);
    }
  };

  /* ── Validations ──────────────────────────────────────────────────── */
  const blockReasons = [
    !name.trim()                && "Le nom de la campagne est requis",
    !subjects.fr.trim()         && "Le sujet en français est requis",
    !bodies.fr.trim()           && "Le contenu HTML en français est requis",
    recipientCount === 0        && "Aucun destinataire ne matche les filtres actuels",
  ].filter(Boolean);

  const canSave = blockReasons.length === 0 && !loading && !isReadOnly;

  /* ── Save (create OU update) ──────────────────────────────────────── */
  const buildPayload = () => {
    const payload = { name, filters };
    LANGS.forEach(l => {
      payload[`subject_${l.code}`]   = subjects[l.code] || "";
      payload[`body_html_${l.code}`] = bodies[l.code]   || "";
    });
    return payload;
  };

  const save = async () => {
    if (!canSave) return;
    setLoading(true); setApiError("");
    try {
      const payload = buildPayload();
      let res;
      if (editing) {
        res = await adminUpdateEmailCampaign(campaign.id, payload);
        toast.ok("Campagne mise à jour.");
      } else {
        res = await adminCreateEmailCampaign(payload);
        toast.ok("Campagne créée en brouillon.");
      }
      onSaved(res.data);
    } catch (err) {
      const msg = extractErr(err);
      setApiError(msg);
      toast.err(msg);
    } finally {
      setLoading(false);
    }
  };

  /* ── Preview ──────────────────────────────────────────────────────── */
  const handlePreview = async () => {
    if (!subjects.fr.trim()) {
      toast.err("Saisis d'abord le sujet en français.");
      return;
    }
    // Save first si nouvelle campagne (preview nécessite un id)
    let id = campaign?.id;
    if (!id) {
      try {
        const r = await adminCreateEmailCampaign(buildPayload());
        id = r.data.id;
        toast.ok("Brouillon sauvegardé.");
        onSaved(r.data);   // notifier le parent
      } catch (err) {
        toast.err(extractErr(err, "Impossible de créer le brouillon."));
        return;
      }
    } else if (editing && !isReadOnly) {
      // Mettre à jour avant le preview pour qu'il reflète les changements
      try {
        await adminUpdateEmailCampaign(id, buildPayload());
      } catch {}
    }

    try {
      const r = await adminPreviewEmailCampaign(id, { language: activeLang });
      setPreviewModal({
        subject:  r.data.subject,
        body:     r.data.body_html,
        language: r.data.language,
        username: r.data.username,
      });
    } catch (err) {
      toast.err(extractErr(err, "Impossible de générer le preview."));
    }
  };

  /* ── Send test (à mon email) ─────────────────────────────────────── */
  const handleSendTest = async () => {
    if (!subjects.fr.trim() || !bodies.fr.trim()) {
      toast.err("Sujet et contenu FR requis.");
      return;
    }
    let id = campaign?.id;
    if (!id) {
      try {
        const r = await adminCreateEmailCampaign(buildPayload());
        id = r.data.id;
        onSaved(r.data);
      } catch (err) {
        toast.err(extractErr(err, "Impossible de créer le brouillon."));
        return;
      }
    } else if (!isReadOnly) {
      try {
        await adminUpdateEmailCampaign(id, buildPayload());
      } catch {}
    }

    try {
      const r = await adminTestEmailCampaign(id, { language: activeLang });
      if (r.data.success) {
        toast.ok(`✓ Email de test envoyé à ${r.data.to_email}`);
      } else {
        toast.err(`Échec : ${r.data.error || "raison inconnue"}`);
      }
    } catch (err) {
      toast.err(extractErr(err, "Impossible d'envoyer le test."));
    }
  };

  /* ── Helpers segment ──────────────────────────────────────────────── */
  const toggleArray = (key, value) => {
    setFilters(f => {
      const list = f[key] || [];
      const has = list.includes(value);
      return { ...f, [key]: has ? list.filter(v => v !== value) : [...list, value] };
    });
  };

  const setFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
  };

  /* ── Helpers booléens tri-state ──────────────────────────────────── */
  const TriRadio = ({ filterKey, label }) => (
    <div style={{display:"flex",gap:".75rem",alignItems:"center",flexWrap:"wrap"}}>
      <Label style={{minWidth:"140px"}}>{label}</Label>
      <Radio $active={filters[filterKey]===null||filters[filterKey]===undefined}>
        <input type="radio" name={filterKey}
          checked={filters[filterKey]===null||filters[filterKey]===undefined}
          onChange={() => setFilter(filterKey, null)}/>
        Tous
      </Radio>
      <Radio $active={filters[filterKey]===true}>
        <input type="radio" name={filterKey}
          checked={filters[filterKey]===true}
          onChange={() => setFilter(filterKey, true)}/>
        Oui
      </Radio>
      <Radio $active={filters[filterKey]===false}>
        <input type="radio" name={filterKey}
          checked={filters[filterKey]===false}
          onChange={() => setFilter(filterKey, false)}/>
        Non
      </Radio>
    </div>
  );

  /* ── Render ────────────────────────────────────────────────────────── */
  return (
    <>
      <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
        <ModalBox>
          <MHead>
            <MTitle>
              {isReadOnly ? "👁 Voir" : editing ? "✎ Éditer" : "+ Nouvelle"} campagne
              {isReadOnly && (
                <span style={{
                  marginLeft:".75rem",
                  fontSize:".7rem",
                  fontFamily:"DM Mono,monospace",
                  color:"#5a6a7a",
                }}>
                  (lecture seule — statut {campaign.status})
                </span>
              )}
            </MTitle>
            <MClose onClick={onClose}>✕</MClose>
          </MHead>

          <MBody>
            {/* ════ NOM ════ */}
            <Field>
              <Label>Nom de la campagne <Required>*</Required></Label>
              <Input
                placeholder="Ex: Newsletter Mars 2026"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={isReadOnly}
                maxLength={200}
              />
            </Field>

            {/* ════ CONTENU MULTILINGUE ════ */}
            <Section>
              <SectionTitle>
                <span>📝 Contenu (multilingue)</span>
                {!isReadOnly && (
                  <div style={{display:"flex",alignItems:"center",gap:".5rem"}}>
                    <label style={{
                      display:"inline-flex",alignItems:"center",gap:".3rem",
                      fontSize:".6rem",color:"#8a9aaa",cursor:"pointer",
                      textTransform:"none",letterSpacing:0,
                    }}>
                      <input type="checkbox"
                        checked={preserveManual}
                        onChange={e => setPreserveManual(e.target.checked)}/>
                      Garder mes traductions
                    </label>
                    <TranslateBtn
                      type="button"
                      onClick={handleAutoTranslateAll}
                      disabled={translating || !subjects.fr.trim()}>
                      {translating ? "Traduction…" : "🪄 Tout traduire depuis FR"}
                    </TranslateBtn>
                  </div>
                )}
              </SectionTitle>

              <LangTabs>
                {LANGS.map(l => (
                  <LangTab key={l.code}
                    type="button"
                    $active={activeLang===l.code}
                    onClick={() => setActiveLang(l.code)}>
                    <LangFlag>{l.flag}</LangFlag>
                    {l.label}
                    {!!subjects[l.code]?.trim() && (
                      <span style={{
                        fontSize:".55rem",
                        color: activeLang===l.code?"#4fc3f7":"#00e676",
                        marginLeft:".15rem",
                      }}>✓</span>
                    )}
                    {autoTranslating && activeLang===l.code && (
                      <span style={{
                        display:"inline-block",width:"6px",height:"6px",
                        borderRadius:"50%",background:"#4fc3f7",
                        marginLeft:".25rem",
                      }}/>
                    )}
                  </LangTab>
                ))}
              </LangTabs>

              <Field>
                <Label>
                  Sujet ({LANGS.find(l => l.code===activeLang)?.label})
                  {activeLang === "fr" && <Required>*</Required>}
                </Label>
                <Input
                  dir={activeLang === "ar" ? "rtl" : "ltr"}
                  placeholder={
                    activeLang === "fr"
                      ? "Ex: Salut {{ first_name }}, on a une grosse news !"
                      : autoTranslating ? "Traduction en cours…" : ""
                  }
                  value={subjects[activeLang] || ""}
                  onChange={e => setSubjects(s => ({...s, [activeLang]: e.target.value}))}
                  disabled={isReadOnly}
                  maxLength={255}
                  style={autoTranslating && !subjects[activeLang]?.trim() ? {opacity:.6} : {}}
                />
              </Field>

              <Field>
                <Label>
                  Contenu HTML ({LANGS.find(l => l.code===activeLang)?.label})
                  {activeLang === "fr" && <Required>*</Required>}
                </Label>
                <Tarea
                  dir={activeLang === "ar" ? "rtl" : "ltr"}
                  placeholder={
                    activeLang === "fr"
                      ? `<h1>Salut {{ first_name }} 👋</h1>\n<p>Voici les nouveautés...</p>\n\nVariables disponibles :\n  {{ first_name }}  →  Maxime\n  {{ username }}    →  maxime\n  {{ email }}       →  max@evptrading.com\n  {{ wallet_balance }} →  150.50`
                      : autoTranslating ? "Traduction en cours…" : ""
                  }
                  value={bodies[activeLang] || ""}
                  onChange={e => setBodies(b => ({...b, [activeLang]: e.target.value}))}
                  disabled={isReadOnly}
                  style={autoTranslating && !bodies[activeLang]?.trim() ? {opacity:.6} : {}}
                />
                <span style={{fontSize:".62rem",color:"#5a6a7a"}}>
                  💡 Variables Django : <code>{`{{ first_name }}`}</code>, <code>{`{{ username }}`}</code>, <code>{`{{ email }}`}</code>, <code>{`{{ wallet_balance }}`}</code>
                </span>
              </Field>
            </Section>

            {/* ════ SEGMENT BUILDER ════ */}
            <Section>
              <SectionTitle>🎯 Ciblage des destinataires</SectionTitle>

              {/* Statut compte */}
              <div style={{display:"flex",gap:".75rem",alignItems:"center",flexWrap:"wrap"}}>
                <Label style={{minWidth:"140px"}}>Statut compte</Label>
                {["all","active","inactive"].map(s => (
                  <Radio key={s} $active={filters.status===s}>
                    <input type="radio" name="status"
                      checked={filters.status===s}
                      onChange={() => setFilter("status", s)}
                      disabled={isReadOnly}/>
                    {s === "all" ? "Tous" : s === "active" ? "Actifs (30j)" : "Inactifs (30j)"}
                  </Radio>
                ))}
              </div>

              {/* Langues */}
              <Field>
                <Label>Langues parlées (vide = toutes)</Label>
                <div style={{display:"flex",gap:".35rem",flexWrap:"wrap"}}>
                  {LANGS.map(l => (
                    <Chip key={l.code} type="button"
                      $active={(filters.languages||[]).includes(l.code)}
                      onClick={() => !isReadOnly && toggleArray("languages", l.code)}
                      disabled={isReadOnly}>
                      <LangFlag>{l.flag}</LangFlag>
                      {l.label}
                    </Chip>
                  ))}
                </div>
              </Field>

              {/* Pays */}
              <Field>
                <Label>
                  Pays (vide = tous)
                  {filters.countries?.length > 0 && (
                    <span style={{
                      marginLeft:".4rem",color:"#4fc3f7",
                      fontFamily:"DM Mono,monospace",fontSize:".6rem",
                    }}>
                      {filters.countries.length} sélectionnés
                    </span>
                  )}
                </Label>
                <div style={{
                  display:"flex",gap:".25rem",flexWrap:"wrap",
                  maxHeight:"120px",overflowY:"auto",
                  padding:".25rem",border:"1px solid #1e2530",borderRadius:"6px",
                }}>
                  {COUNTRIES.map(c => (
                    <Chip key={c.code} type="button"
                      $active={(filters.countries||[]).includes(c.code)}
                      onClick={() => !isReadOnly && toggleArray("countries", c.code)}
                      disabled={isReadOnly}
                      style={{fontSize:".62rem"}}>
                      {c.code} {c.name}
                    </Chip>
                  ))}
                </div>
              </Field>

              {/* Dates inscription */}
              <Row2>
                <Field>
                  <Label>Inscrit après</Label>
                  <Input type="date"
                    value={filters.registered_after || ""}
                    onChange={e => setFilter("registered_after", e.target.value || null)}
                    disabled={isReadOnly}/>
                </Field>
                <Field>
                  <Label>Inscrit avant</Label>
                  <Input type="date"
                    value={filters.registered_before || ""}
                    onChange={e => setFilter("registered_before", e.target.value || null)}
                    disabled={isReadOnly}/>
                </Field>
              </Row2>

              {/* Tri-state booléens */}
              <TriRadio filterKey="has_traded"     label="A déjà tradé"/>
              <TriRadio filterKey="has_deposited"  label="A déjà déposé"/>
              <TriRadio filterKey="phone_verified" label="Téléphone vérifié"/>

              {/* Balance */}
              <Row2>
                <Field>
                  <Label>Solde min (EVP)</Label>
                  <Input type="number" min="0" step="1"
                    placeholder="ex: 100"
                    value={filters.balance_min ?? ""}
                    onChange={e => setFilter("balance_min", e.target.value ? parseFloat(e.target.value) : null)}
                    disabled={isReadOnly}/>
                </Field>
                <Field>
                  <Label>Solde max (EVP)</Label>
                  <Input type="number" min="0" step="1"
                    placeholder="laisser vide = ∞"
                    value={filters.balance_max ?? ""}
                    onChange={e => setFilter("balance_max", e.target.value ? parseFloat(e.target.value) : null)}
                    disabled={isReadOnly}/>
                </Field>
              </Row2>

              {/* Volume trades */}
              <Field>
                <Label>Volume de trade min (EVP)</Label>
                <Input type="number" min="0" step="1"
                  placeholder="ex: 50"
                  value={filters.trade_volume_min ?? ""}
                  onChange={e => setFilter("trade_volume_min", e.target.value ? parseFloat(e.target.value) : null)}
                  disabled={isReadOnly}/>
              </Field>

              {/* Options */}
              <div style={{display:"flex",gap:"1rem",flexWrap:"wrap"}}>
                <Radio $active={filters.include_admins}>
                  <input type="checkbox"
                    checked={!!filters.include_admins}
                    onChange={e => setFilter("include_admins", e.target.checked)}
                    disabled={isReadOnly}/>
                  Inclure les admins
                </Radio>
                <Radio $active={filters.exclude_unsubscribed !== false}>
                  <input type="checkbox"
                    checked={filters.exclude_unsubscribed !== false}
                    onChange={e => setFilter("exclude_unsubscribed", e.target.checked)}
                    disabled={isReadOnly}/>
                  Exclure les désabonnés
                </Radio>
              </div>
            </Section>

            {/* ════ COMPTEUR LIVE ════ */}
            <Counter>
              <div>
                <CountVal $zero={recipientCount === 0}>
                  {counting ? "…" : recipientCount === null ? "—" : recipientCount.toLocaleString("fr-FR")}
                </CountVal>{" "}
                <CountLbl>destinataires correspondent aux filtres</CountLbl>
              </div>
              {previewSample.length > 0 && (
                <div style={{
                  fontSize:".68rem",color:"#5a6a7a",
                  fontFamily:"DM Mono,monospace",textAlign:"right",
                }}>
                  Exemples : {previewSample.slice(0, 3).map(p => p.username).join(", ")}…
                </div>
              )}
            </Counter>

            {/* ════ ERREURS / WARNINGS ════ */}
            {blockReasons.length > 0 && !isReadOnly && (
              <div style={{
                fontSize:".72rem",color:"#ffc107",
                background:"rgba(255,193,7,.06)",
                border:"1px solid #ffc10733",
                borderRadius:"7px",padding:".65rem .85rem",
                display:"flex",flexDirection:"column",gap:".25rem",
              }}>
                {blockReasons.map((r, i) => <span key={i}>⚠ {r}</span>)}
              </div>
            )}

            {apiError && (
              <div style={{
                fontSize:".75rem",color:"#ff3b5c",
                background:"#ff3b5c11",
                border:"1px solid #ff3b5c33",
                borderRadius:"7px",padding:".65rem .85rem",
              }}>
                ✕ {apiError}
              </div>
            )}
          </MBody>

          <MFoot>
            <CancelBtn onClick={onClose}>
              {isReadOnly ? "Fermer" : "Annuler"}
            </CancelBtn>

            {!isReadOnly && (
              <>
                <SaveBtn $variant="test" onClick={handleSendTest}
                  disabled={!subjects.fr.trim() || !bodies.fr.trim() || loading}>
                  📨 Test à moi
                </SaveBtn>
                <SaveBtn $variant="test" onClick={handlePreview}
                  disabled={!subjects.fr.trim() || loading}>
                  👁 Aperçu
                </SaveBtn>
                <SaveBtn onClick={save} disabled={!canSave}>
                  {loading ? "…" : editing ? "Enregistrer" : "Créer brouillon"}
                </SaveBtn>
              </>
            )}
          </MFoot>
        </ModalBox>
      </Overlay>

      {/* PREVIEW MODAL */}
      {previewModal && (
        <Overlay onClick={e => e.target === e.currentTarget && setPreviewModal(null)}>
          <ModalBox style={{maxWidth:"700px"}}>
            <MHead>
              <MTitle>
                👁 Aperçu —{" "}
                <span style={{color:"#4fc3f7"}}>{previewModal.language?.toUpperCase()}</span>
                {previewModal.username && (
                  <span style={{
                    marginLeft:".5rem",fontSize:".7rem",
                    fontFamily:"DM Mono,monospace",color:"#8a9aaa",
                  }}>
                    pour {previewModal.username}
                  </span>
                )}
              </MTitle>
              <MClose onClick={() => setPreviewModal(null)}>✕</MClose>
            </MHead>
            <MBody>
              <Field>
                <Label>Sujet</Label>
                <div style={{
                  padding:".55rem .85rem",
                  background:"#080a0c",
                  border:"1px solid #1e2530",
                  borderRadius:"6px",
                  fontSize:".85rem",fontWeight:600,
                }}>
                  {previewModal.subject}
                </div>
              </Field>
              <Field>
                <Label>Corps rendu</Label>
                <PreviewBox>
                  <iframe
                    title="Email preview"
                    srcDoc={previewModal.body}
                    style={{
                      width:"100%",
                      height:"500px",
                      border:"none",
                      display:"block",
                    }}
                  />
                </PreviewBox>
              </Field>
            </MBody>
            <MFoot>
              <CancelBtn onClick={() => setPreviewModal(null)}>Fermer</CancelBtn>
            </MFoot>
          </ModalBox>
        </Overlay>
      )}
    </>
  );
}